// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import 'app/common/react/components/growth/activation/NuxContainer/NuxContainer.scss';
import * as StringUtil from 'app/common/lib/StringUtil';
import doesContainObjectById from 'app/common/react/components/growth/activation/NuxUtils/NuxUtils';
import HistoryNavigator from 'app/common/lib/HistoryNavigator';
import i18n from 'app/common/lib/i18n';
import NavigateData from 'app/common/lib/NavigateData';
import NuxCountryStep from 'app/common/react/components/growth/activation/NuxLocaleCountryStep/NuxCountryStep';
import NuxEducationLoader from 'app/common/react/components/growth/activation/NuxEducationLoader/NuxEducationLoader';
import NuxExtensionUpsell from 'app/common/react/components/growth/activation/NuxExtensionUpsell/NuxExtensionUpsell';
import NuxGenderStep from 'app/common/react/components/growth/activation/NuxGenderStep/NuxGenderStep';
import NuxLocaleCountryStep from 'app/common/react/components/growth/activation/NuxLocaleCountryStep/NuxLocaleCountryStep';
import NuxLocaleStep from 'app/common/react/components/growth/activation/NuxLocaleCountryStep/NuxLocaleStep';
import NuxModal from 'app/common/react/components/growth/activation/NuxModal/NuxModal';
import NuxPicker from 'app/common/react/components/growth/activation/NuxPicker/NuxPicker';
import NuxWelcomeNameStep from 'app/common/react/components/growth/activation/NuxWelcomeNameStep/NuxWelcomeNameStep';
import type { Router } from 'app/common/lib/ReactRouter/types';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import withDefaultNuxSteps from 'app/common/react/components/growth/activation/withDefaultNuxSteps/withDefaultNuxSteps';
import withFilteredNuxSteps from 'app/common/react/components/growth/activation/withFilteredNuxSteps/withFilteredNuxSteps';
import { MultiTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import { withRouter } from 'react-router';
import setNuxSelectedInterestImages, {
  type ImageDimensionsInfo,
} from 'app/common/redux/actions/nuxSelectedInterestImages';
import withViewer, { type Viewer } from 'app/common/lib/react/withViewer';
import {
  DEFAULT_NUM_TO_FOLLOW,
  FOLLOW_URSS_TOPICS,
  RENUX_NUM_TO_FOLLOW,
  type NuxStep,
} from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import {
  updateFirstHomeFeedRequestAfterNuxFlag,
  submitOrientationContextFollow,
} from 'app/common/redux/actions/nux/updateUser';
import {
  withRequestContext,
  RequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { compose, type Dispatch } from 'redux';

/**
 * Nux (a.k.a. Orientation) Steps
 *
 *    const steps = {
 *      0: {
 *           moduleName: 'InterestPickerStep',
 *           options: {},
 *         },
 *      1: {
 *           moduleName: 'NuxExtensionUpsell',
 *           options: {},
 *         },
 *      2: {
 *           moduleName: 'NuxEducationLoader',
 *           options: {},
 *         },
 *    };
 */

type OwnProps = {|
  handleComplete: () => void,
  handleDismiss?: () => void,
  isLandingPage?: boolean,
  isRenux?: boolean,
  isSimpleRenux?: boolean, // only has [Picker, Loader], used by topic education
  steps: Array<NuxStep>,
|};

type StateProps = {|
  isPartner: boolean,
|};

type DispatchProps = {|
  dispatchSetNuxSelectedInterestImages: (Array<ImageDimensionsInfo>) => void,
  dispatchSubmitOrientationContextFollow: () => Promise<any>,
  dispatchUpdateFirstHomeFeedRequestAfterNuxFlag: (boolean) => void,
|};

type HOCProps = {|
  requestContext: RequestContextType,
  router: Router,
  viewer: Viewer,
|};

type ConnectProps = {|
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
|};

type Props = {| ...OwnProps, ...StateProps, ...DispatchProps, ...HOCProps |};

export type SelectedInterest = {
  backgroundColor: ?string,
  contextLocale: ?string,
  followerCount: ?number,
  id: string,
  images: {
    '400x': {
      height: number,
      url: string,
      width: number,
    },
  },
  is_followed: boolean,
  label: string,
  l1Name: ?string,
};

type FollowedInterest = {};

type State = {
  // previously followed interests plus newly selected interests
  followedInterests: Array<FollowedInterest>,
  isLoading: boolean,
  nuxStep: number,
  // currently selected interests
  selectedInterests: Array<SelectedInterest>,
  // If the NUX back button has been clicked during this session
  userClickedBack: boolean,
};

export class NuxContainer extends Component<Props, State> {
  static defaultProps = {
    isRenux: false,
    isSimpleRenux: false,
  };
  state = {
    followedInterests: [],
    isLoading: false,
    nuxStep: this.getNextStepIndex(-1, this.props.steps, false),
    selectedInterests: [],
    userClickedBack: false,
  };

  componentDidMount() {
    const { isRenux } = this.props;

    this.mounted = true;

    track(PCONST.thrift.EventType.NUX_START, {
      view: isRenux ? PCONST.thrift.ViewType.REDO_ORIENTATION : PCONST.thrift.ViewType.ORIENTATION,
      viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_START,
    });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  onComplete = () => {
    const {
      handleComplete,
      isLandingPage,
      isRenux,
      dispatchUpdateFirstHomeFeedRequestAfterNuxFlag,
    } = this.props;
    track(PCONST.thrift.EventType.NUX_END, {
      view: isRenux ? PCONST.thrift.ViewType.REDO_ORIENTATION : PCONST.thrift.ViewType.ORIENTATION,
      viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_END,
    });

    if (!isRenux) {
      dispatchUpdateFirstHomeFeedRequestAfterNuxFlag(true);
    }

    handleComplete();

    if (isLandingPage) {
      this.handleNavigate('/');
    }
  };

  getUpdatedFollowInterests(
    followedInterests: Array<FollowedInterest>,
    newInterests: Array<SelectedInterest>,
    isSelected: boolean,
  ) {
    let updatedInterestFollowStatus = [...followedInterests];
    newInterests.forEach((newInterest) => {
      const newOrUpdatedInterest = {
        ...newInterest,
        ...{ is_followed: isSelected },
      };

      let updated = false;
      updatedInterestFollowStatus = updatedInterestFollowStatus.map((interest) => {
        if (interest.id === newOrUpdatedInterest.id) {
          updated = true;
          return newOrUpdatedInterest;
        }
        return interest;
      });
      if (!updated) {
        updatedInterestFollowStatus.push(newOrUpdatedInterest);
      }
    });

    return updatedInterestFollowStatus;
  }

  getUpdatedSelectedInterests(
    currentInterests: Array<SelectedInterest>,
    newInterests: Array<SelectedInterest>,
    isSelected: boolean,
  ) {
    let updatedSelectedInterests = [...currentInterests];
    newInterests.forEach((newInterest) => {
      const doesContain = doesContainObjectById(updatedSelectedInterests, newInterest);

      // if the interest is selected && unique to the list
      if (isSelected && !doesContain) {
        updatedSelectedInterests.push({
          ...newInterest,
          ...{ is_followed: isSelected },
        });

        // if the interest is deselected && part of the list
      } else if (!isSelected && doesContain) {
        updatedSelectedInterests = currentInterests.filter(
          (object) => object.id !== newInterest.id,
        );
      }
    });

    return updatedSelectedInterests;
  }

  getPersonalizationCopy(currentStep: NuxStep) {
    const { isRenux, isSimpleRenux, requestContext, steps } = this.props;

    let headerText, subHeaderText;
    const currentModuleName = currentStep.moduleName;
    const stepNames = steps.map((step) => step.moduleName);
    const currentStepIndex = stepNames.indexOf(currentModuleName);
    const isLastControllableStep = currentStepIndex === steps.length - 2;

    switch (currentModuleName) {
      case 'NuxWelcomeNameStep':
        headerText = i18n._('Welcome to Pinterest', 'greeting on welcome name step');
        if (
          new MultiTreatmentExp(requestContext, 'web_nux_prevent_duplicates').check('enabled_copy')
        ) {
          headerText = i18n._(
            'Welcome to your new Pinterest account',
            'greeting on welcome name step for new accounts',
          );
        }
        return {
          headerText,
          subHeaderText: i18n._(
            'Your answers to the next few questions will help us find the right ideas for you',
            'header on nux welcome and name editing step',
          ),
        };
      case 'NuxGenderStep':
        headerText = i18n._('How do you identify?', 'Header in NUX gender step.');
        subHeaderText = '';
        if (isRenux) {
          headerText = i18n._(
            'It’s time to refresh your feed!',
            "Title on the modal asking user's gender",
          );
          subHeaderText = i18n._(
            'How would you describe yourself?',
            "Subtitle on the modal asking user's gender",
          );
        }
        return {
          headerText,
          subHeaderText,
        };
      case 'NuxLocaleCountryStep':
        return {
          headerText: i18n._('Pick your language and country', 'Header in NUX locale/country step'),
        };
      case 'NuxLocaleStep':
        return {
          headerText: i18n._('Pick your language', 'Header in NUX locale step'),
        };
      case 'NuxCountryStep':
        return {
          headerText: i18n._('Pick your country', 'Header in NUX country step'),
        };
      case 'NuxPicker':
        if (isRenux && !isSimpleRenux) {
          headerText = i18n._(
            'What are you into these days?',
            'Header in ReNUX topic picker step.',
          );
          subHeaderText = i18n._(
            'Pick the topics you want to see in your home feed',
            'Subheader in ReNUX topic picker step.',
          );
        } else {
          headerText = !isLastControllableStep
            ? i18n._("Tell us what you're interested in", 'Header in NUX topic picker step.')
            : i18n._(
                "Last step! Tell us what you're interested in",
                'Header in NUX topic picker step.',
              );
          subHeaderText = StringUtil.dangerouslyFormat(
            i18n._(
              'Pick {{ numberToFollow }} (or more!) to start discovering new ideas',
              'Subheader in NUX topic picker step with required number of topics to follow',
            ),
            { numberToFollow: String(DEFAULT_NUM_TO_FOLLOW) },
          );
        }

        return {
          headerText,
          subHeaderText,
        };
      case 'NuxExtensionUpsell':
        headerText = !isLastControllableStep
          ? i18n._('Get our handy browser button', 'Header in NUX browser extension upsell step.')
          : i18n._(
              'Last step! Get our handy browser button',
              'Header in NUX browser extension upsell step.',
            );

        return {
          headerText,
          subHeaderText: i18n._(
            'Quickly save any idea you find around the web',
            'Subheader in NUX browser extension upsell step',
          ),
        };
      default:
        return {};
    }
  }

  mounted = false;

  handleNavigate = (url: string) => {
    const { router: reactRouter } = this.props;

    if (reactRouter) {
      reactRouter.push(url);
    } else {
      const _navigateData = new NavigateData();
      HistoryNavigator.navigate(url, _navigateData);
    }
  };

  handleSelectedInterestsChange = (newInterests: Array<SelectedInterest>, isSelected: boolean) => {
    const getInterestState = (prevState: State, props: Props) => {
      const updatedSelectedInterests = this.getUpdatedSelectedInterests(
        prevState.selectedInterests,
        newInterests,
        isSelected,
      );
      const updatedFollowedInterests = this.getUpdatedFollowInterests(
        prevState.followedInterests,
        newInterests,
        isSelected,
      );

      const { dispatchSetNuxSelectedInterestImages, isRenux } = props;
      if (!isRenux) {
        // Set list of updated selected interests for displaying in background images during topic picker
        // and loader step
        dispatchSetNuxSelectedInterestImages(
          updatedSelectedInterests.reduce((selectedInterestImages, interest) => {
            if (interest && interest.images && interest.images['400x']) {
              const selectedInterestImage = {
                height: interest.images['400x'].height,
                width: interest.images['400x'].width,
                url: interest.images['400x'].url,
              };
              selectedInterestImages.push(selectedInterestImage);
            }
            return selectedInterestImages;
          }, []),
        );
      }

      return {
        selectedInterests: updatedSelectedInterests,
        followedInterests: updatedFollowedInterests,
      };
    };

    this.setState(getInterestState);
  };

  nuxSteps = {
    NuxLocaleStep,
    NuxCountryStep,
    NuxGenderStep,
    NuxLocaleCountryStep,
    NuxPicker,
    NuxExtensionUpsell,
    NuxEducationLoader,
    NuxWelcomeNameStep,
  };

  nuxStepToRender(currentStep: NuxStep, showTableOfContents: boolean) {
    const { followedInterests, selectedInterests } = this.state;
    const { isRenux, isSimpleRenux } = this.props;
    const denzelModule = currentStep.moduleName;
    const NuxContainerContentComponent = this.nuxSteps[denzelModule];
    const hasGenderStep = this.hasNuxStep('NuxGenderStep');
    // TODO: Fix partnerWebsiteHostname to receive the hostname instead of a hardcoded string
    return (
      <RequestContext>
        {(requestContext) => (
          <NuxContainerContentComponent
            {...currentStep.options}
            experiments={requestContext.experiments}
            followedInterests={followedInterests}
            hasGenderStep={hasGenderStep}
            incrementNuxStep={this.incrementNuxStep(this.state.nuxStep)}
            isSimpleRenux={isSimpleRenux}
            isRenux={isRenux}
            numberToFollow={isRenux && !isSimpleRenux ? RENUX_NUM_TO_FOLLOW : DEFAULT_NUM_TO_FOLLOW}
            onSelectedInterestsChange={this.handleSelectedInterestsChange}
            personalizationCopy={this.getPersonalizationCopy(currentStep)}
            requestContext={requestContext}
            selectedInterests={selectedInterests}
            showTableOfContents={showTableOfContents}
            toggleLoadingIndicator={(on: boolean) => this.setState({ isLoading: on })}
          />
        )}
      </RequestContext>
    );
  }

  getNextStepIndex(currentStep: number, steps: Array<NuxStep>, userClickedBack: boolean) {
    const { isRenux } = this.props;

    let nextStepIndex = currentStep + 1;
    if (userClickedBack) {
      // If went back, can only go forward one step at a time
      // This is important because otherwise user can go back 2+ times
      // And hitting next would skip a couple of steps (since already completed)
      return nextStepIndex;
    }
    if (!isRenux) {
      // Only check step completion state for NUX
      // Move to first incomplete step
      while (nextStepIndex < steps.length && steps[nextStepIndex].isCompleted) {
        nextStepIndex += 1;
      }
    }
    return nextStepIndex;
  }

  incrementNuxStep(currentStep: number) {
    const { steps, dispatchSubmitOrientationContextFollow } = this.props;
    const { userClickedBack } = this.state;

    const nextStepIndex = this.getNextStepIndex(currentStep, steps, userClickedBack);
    if (nextStepIndex >= steps.length) {
      return this.onComplete;
    }

    return () => {
      const setStateFn = (nextStepIndex) => {
        if (currentStep !== nextStepIndex) {
          return this.setState({ nuxStep: nextStepIndex });
        }
        return undefined;
      };
      if (
        steps.length > nextStepIndex &&
        steps[nextStepIndex].options &&
        steps[nextStepIndex].options.onStartApi
      ) {
        const onStartApi = steps[nextStepIndex].options.onStartApi;
        if (onStartApi === FOLLOW_URSS_TOPICS) {
          dispatchSubmitOrientationContextFollow().finally(setStateFn);
        } else {
          setStateFn(nextStepIndex);
        }
      } else {
        setStateFn(nextStepIndex);
      }
    };
  }

  hasNuxStep(moduleName: string) {
    const { steps } = this.props;
    return steps.some((step) => step.moduleName === moduleName);
  }

  onPrevStepClickFn = (currentStep: number) => {
    const { steps, isRenux } = this.props;
    const moduleName = steps[currentStep].moduleName;
    return () => {
      const moduleNameToViewParameter = {
        NuxWelcomeNameStep: PCONST.thrift.ViewParameterType.ORIENTATION_WELCOME,
        NuxGenderStep: PCONST.thrift.ViewParameterType.ORIENTATION_GENDER_STEP,
        NuxLocaleCountryStep: PCONST.thrift.ViewParameterType.ORIENTATION_LOCALE_COUNTRY_STEP,
        NuxPicker: PCONST.thrift.ViewParameterType.ORIENTATION_INTEREST_PICKER,
      };
      const viewParameterToLog = moduleNameToViewParameter[moduleName];
      if (viewParameterToLog) {
        // For now, only log if we know what step it is.
        // Don't want NUX to error just because of logging
        track(PCONST.thrift.EventType.CLICK, {
          component: PCONST.thrift.ComponentType.BROWSER,
          element: PCONST.thrift.ElementType.BACK_BUTTON,
          view: isRenux
            ? PCONST.thrift.ViewType.REDO_ORIENTATION
            : PCONST.thrift.ViewType.ORIENTATION,
          viewParameter: viewParameterToLog,
        });
      }
      if (currentStep > 0) {
        // This should always be true because Back button should not exist for the first step
        this.setState({ nuxStep: currentStep - 1, userClickedBack: true });
      }
    };
  };

  render() {
    const { isLoading, nuxStep } = this.state;
    const {
      handleDismiss,
      isLandingPage,
      isPartner,
      isRenux,
      isSimpleRenux,
      requestContext,
      steps,
    } = this.props;

    const isLoadingStep = (step) => ['NuxEducationLoader'].includes(step);
    const actionSteps = steps.filter((step) => !isLoadingStep(step.moduleName));

    const showCounterOrTable =
      !isLoadingStep(steps[nuxStep].moduleName) && !isLandingPage && !isRenux;
    const showTable = showCounterOrTable && actionSteps.length > 1 && !isPartner;
    const showCounter = showCounterOrTable && !showTable;
    const displayHide = !!(isRenux && !isLoadingStep(steps[nuxStep].moduleName));

    return (
      <NuxModal
        displayHide={displayHide}
        isLoading={isLoading}
        isRenux={isRenux}
        isSimpleRenux={isSimpleRenux}
        nuxStep={steps[nuxStep]}
        onHide={handleDismiss}
        onPrevStepClick={this.onPrevStepClickFn(nuxStep)}
        requestContext={requestContext}
        showCounter={showCounter}
        showTableContents={showTable}
        steps={actionSteps}
      >
        {this.nuxStepToRender(steps[nuxStep], showTable)}
      </NuxModal>
    );
  }
}

export default compose(
  withRequestContext,
  connect<ConnectProps, OwnProps, StateProps, DispatchProps, _, _>(
    ({ viewer }: ReduxState): StateProps => ({
      isPartner: viewer.isAuth && viewer.isPartner,
    }),
    (dispatch: Dispatch<any>): DispatchProps => ({
      dispatchSetNuxSelectedInterestImages: (images: Array<ImageDimensionsInfo>) =>
        dispatch(setNuxSelectedInterestImages(images)),
      dispatchSubmitOrientationContextFollow: () => dispatch(submitOrientationContextFollow()),
      dispatchUpdateFirstHomeFeedRequestAfterNuxFlag: (enabled) =>
        dispatch(updateFirstHomeFeedRequestAfterNuxFlag(enabled)),
    }),
  ),
  withViewer,
  withFilteredNuxSteps,
  withDefaultNuxSteps,
  withRouter,
)(NuxContainer);
